// Client for the time-entry document routes
// (`/accounts/:accountId/activities/:activityId/documents[...]`). Mirrors the
// supplier-invoice document client; the response shape comes from @packages/types.
import { ActivityDocumentResponseSchema, ActivityDocumentListResponseSchema, } from '@packages/types';
import { api, apiRaw, getApiUrl, getApiBridge, parseErrorResponse, filenameFromResponse } from '../client.js';
/**
 * Upload a file to a time entry (activity).
 * Uses raw fetch with FormData - the api() wrapper is JSON-only.
 */
export async function uploadActivityDocument(accountId, activityId, file) {
    const bridge = getApiBridge();
    const baseUrl = getApiUrl();
    const token = bridge.getToken();
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${baseUrl}/accounts/${accountId}/activities/${activityId}/documents`, {
        method: 'POST',
        headers,
        body: formData,
    });
    if (response.status === 401) {
        bridge.removeToken();
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
    const json = await response.json();
    const result = ActivityDocumentResponseSchema.safeParse(json);
    if (!result.success) {
        throw new Error('Invalid response from server');
    }
    return result.data;
}
/** List the files on a time entry (activity). */
export function listActivityDocuments(accountId, activityId) {
    return api(`/accounts/${accountId}/activities/${activityId}/documents`, ActivityDocumentListResponseSchema);
}
/** Remove a file from a time entry (activity). */
export async function deleteActivityDocument(accountId, activityId, documentId) {
    await apiRaw(`/accounts/${accountId}/activities/${activityId}/documents/${documentId}`, { method: 'DELETE' });
}
/**
 * Constructs the authenticated download URL for a time-entry file.
 * This URL requires a Bearer token - it cannot be used as a plain <a href>.
 * Use downloadActivityDocument() to fetch with auth and open the file.
 */
export function getActivityDocumentDownloadUrl(accountId, activityId, documentId) {
    return `${getApiUrl()}/accounts/${accountId}/activities/${activityId}/documents/${documentId}/download`;
}
/**
 * Fetches a file as a Blob with auth headers and opens it for preview.
 * Tries window.open first (opens in new tab for PDFs/images), falls back to
 * <a download> click if the popup is blocked.
 */
export async function downloadActivityDocument(accountId, activityId, documentId) {
    const bridge = getApiBridge();
    const token = bridge.getToken();
    if (!token) {
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    const url = getActivityDocumentDownloadUrl(accountId, activityId, documentId);
    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) {
        bridge.removeToken();
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    // Try opening in a new tab (best for PDF/image preview)
    const newTab = window.open(blobUrl, '_blank');
    if (!newTab) {
        // Popup was blocked - fall back to <a download> click
        const filename = filenameFromResponse(response, 'document');
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
